import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import { isAskAnonymously } from '@/util/userFilters';
import { isMedicalOnDemandExpert } from '@/utils/expertHelpers';

export type DirectoryExpertItem = {
  type: 'expert';
  data: ExpertProfile;
};

export type DirectoryProductItem = {
  type: 'product';
  data: ExpertProfile;
};

export type DirectoryAnonymousItem = {
  type: 'anonymous';
  data: { id: string | number };
};

export type DirectoryEmptyItem = {
  type: 'empty';
  id: string;
};

export type DirectoryItem =
  | DirectoryExpertItem
  | DirectoryProductItem
  | DirectoryAnonymousItem
  | DirectoryEmptyItem;

/**
 * Identifies regular medical expert items in the directory list.
 * Used to apply expert-specific rendering (profile navigation, info badges, focus areas).
 */
export function isExpertItem(item: DirectoryItem): item is DirectoryExpertItem {
  return item.type === 'expert';
}

/**
 * Identifies product support expert items (Medical On Demand).
 * Used to apply product-specific rendering (success badges, hardcoded response time, MOD navigation).
 */
export function isProductItem(item: DirectoryItem): item is DirectoryProductItem {
  return item.type === 'product';
}

/**
 * Identifies the "Ask Anonymously" option in the directory list.
 * This item always appears first and allows users to submit anonymous inquiries.
 */
export function isAnonymousItem(item: DirectoryItem): item is DirectoryAnonymousItem {
  return item.type === 'anonymous';
}

/**
 * Identifies actual profile items (expert, product, or anonymous) vs empty placeholder items.
 * Used to filter out grid layout placeholders when counting or processing real profiles.
 */
export function isProfileItem(
  item: DirectoryItem
): item is DirectoryExpertItem | DirectoryProductItem | DirectoryAnonymousItem {
  return item.type === 'expert' || item.type === 'product' || item.type === 'anonymous';
}

/**
 * Identifies empty placeholder items used to fill incomplete rows in multi-column grid layouts.
 * These items are invisible and used only for consistent grid alignment.
 */
export function isEmptyItem(item: DirectoryItem): item is DirectoryEmptyItem {
  return item.type === 'empty';
}

/**
 * Applies the ordering rules to the directory's profiles:
 * 1. Anonymous profile always appears first (as 'anonymous' type)
 * 2. Product Support (Medical On Demand) experts are hidden on default load (no search/filters)
 * 3. Product Support experts appear in natural Algolia ranking when search/filters are active
 * 4. Adds empty placeholder items to fill incomplete rows in multi-column grid layouts
 *
 * Returns a discriminated union array where each item has a `type` field:
 * - `type: 'anonymous'` - Anonymous "Ask Anonymously" option with `id` in `data`
 * - `type: 'expert'` - Regular expert with full profile in `data`
 * - `type: 'product'` - Product support expert with full profile in `data`
 * - `type: 'empty'` - Placeholder item with `id` field
 *
 * Layout parity note:
 * - Non-MOD layouts render this array directly.
 * - MOD split layout derives expert/product lanes from this array.
 * This keeps "Ask Anonymously" sourced from the same filtered-result rule in both paths.
 */
export function orderDirectoryProfiles(
  filteredProfiles: ExpertProfile[],
  searchQuery: string,
  focusId: string,
  productTypeId: string,
  numColumns: number
): DirectoryItem[] {
  const isDefaultLoad = !searchQuery && !focusId && !productTypeId;

  const profilesToProcess = isDefaultLoad
    ? filteredProfiles.filter((profile) => !isMedicalOnDemandExpert(profile))
    : filteredProfiles;

  const currentAnonymousProfile = profilesToProcess.find((profile) =>
    isAskAnonymously(profile.name)
  );

  const regularProfileItems: (DirectoryExpertItem | DirectoryProductItem)[] = profilesToProcess
    .filter((profile) => !isAskAnonymously(profile.name))
    .map((profile) =>
      isMedicalOnDemandExpert(profile)
        ? { type: 'product', data: profile }
        : { type: 'expert', data: profile }
    );

  const anonymousId = currentAnonymousProfile?.id;

  const profileItems: (DirectoryExpertItem | DirectoryProductItem | DirectoryAnonymousItem)[] =
    anonymousId != null
      ? [{ type: 'anonymous', data: { id: anonymousId } }, ...regularProfileItems]
      : regularProfileItems;

  const remainder = profileItems.length % numColumns;

  const fillerCount = numColumns > 1 && remainder > 0 ? numColumns - remainder : 0;

  const fillerItems: DirectoryEmptyItem[] = Array.from({ length: fillerCount }, (_, index) => ({
    type: 'empty',
    id: `empty-${index}`,
  }));

  return [...profileItems, ...fillerItems];
}
